import React, { ReactElement, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useNewSkin from '../../hooks/useNewSkin';
import usePriceFormatter from './usePriceFormatter';

import { getFullParsedUrl, isInSandboxMode } from '../../helpers';
import { ravenCaptureException } from '../../helpers/raven';
import { shopifyAddToCartRequest } from '../../helpers/shopifyApiData';
import { trans } from '../../helpers/translations';

import {
    productRecommendationClicked,
    sendMessageFromVisitorWithBotPayload,
    widgetActivityTracking,
} from '../../store/actions';
import { trackingEvents } from '../../store/activityTrackingHelpers';
import { getKeyFromStorage, removeKeyFromStorage, saveKeyToStorage } from '../../store/savedState';
import { getIsMobile } from '../../store/selectors';
import { RecommendedProduct } from '../../store/typings';
import { getTextStyles } from '../../styles/text.styles';
import Translation from '../Translation';
import AddToCartButton from './AddToCartButton';
import styles from './ProductCard.styles';
import { LEARN_MORE_CLICKED_KEY } from './contants';

interface ProductCardProps extends RecommendedProduct {
    buttonPayload: string;
    chatBotId: number;
    isAddToCartEnabled: boolean;
    messageId: string;
    variantId: number;
    isFlyMessage?: boolean;
}

const ProductCard = ({
    id,
    title,
    imageUrl,
    price,
    currency,
    variants,
    url,
    buttonPayload,
    chatBotId,
    isAddToCartEnabled,
    messageId,
    variantId,
    isFlyMessage,
}: ProductCardProps): ReactElement => {
    const dispatch = useDispatch();
    const isMobile = useSelector(getIsMobile);
    const cardKey = `${messageId}_${id}`;
    const parsedUrl = getFullParsedUrl(url);
    const withAddToCartButton = variants.length <= 1;
    const productAddedToCartTimerRef = useRef<ReturnType<typeof setTimeout>>();
    const shouldSendDataAboutLearnMoreClickedRef = useRef<string | undefined | null>(
        getKeyFromStorage(LEARN_MORE_CLICKED_KEY),
    );
    const { isNewSkin } = useNewSkin();
    const textStyles = getTextStyles(isNewSkin);
    const formatter = usePriceFormatter(currency);

    const addedToCartComplete = useCallback((): void => {
        dispatch(
            productRecommendationClicked({
                productId: id,
                chatBotId,
            }),
        );
    }, [dispatch, id, chatBotId]);

    const handleAddToCart = useCallback(async (): Promise<boolean> => {
        dispatch(
            sendMessageFromVisitorWithBotPayload(
                `${title} &rarr; ${trans('recommendProductAddToCart', null, 'Add to cart')}`,
                buttonPayload,
            ),
        );
        dispatch(widgetActivityTracking(trackingEvents.productRecommendationAddToCartClicked));
        if (isInSandboxMode()) {
            addedToCartComplete();
            return true;
        }

        try {
            const response = await shopifyAddToCartRequest(variantId);
            if (!response.ok) {
                ravenCaptureException('Shopify add to cart request failed', {
                    message: response.statusText,
                });
                return false;
            }
            addedToCartComplete();
            return true;
        } catch (error) {
            if (error instanceof Error) {
                ravenCaptureException('Shopify add to cart request failed', {
                    message: error.message,
                });
            }
            return false;
        }
    }, [dispatch, addedToCartComplete, buttonPayload, variantId, title]);

    const handleLearnMoreDataSend = useCallback((): void => {
        dispatch(
            sendMessageFromVisitorWithBotPayload(
                `${title} &rarr; ${trans('recommendProductLearnMore', null, 'Learn more')}`,
                buttonPayload,
            ),
        );
        dispatch(
            productRecommendationClicked({
                productId: id,
                chatBotId,
            }),
        );
        dispatch(widgetActivityTracking(trackingEvents.productRecommendationLearnMoreClicked));
    }, [buttonPayload, chatBotId, dispatch, id, title]);

    const handleLearnMore = (): void => {
        try {
            if (url.indexOf('://') === -1 && window.top) {
                // we want to store the id of the product which was clicked, so we populate proper data on widget load
                // because all rendered cards will check the value
                const flagStored = saveKeyToStorage(LEARN_MORE_CLICKED_KEY, cardKey);
                // if we fail to store the flag, we want to try to send frames data, maybe it will work
                if (!flagStored) {
                    handleLearnMoreDataSend();
                }
                window.top.location.assign(url);
            } else {
                handleLearnMoreDataSend();
                window.open(parsedUrl);
            }
        } catch {
            handleLearnMoreDataSend();
            window.open(parsedUrl);
        }
    };

    // when we change current location, it's 50/50 if the frames reach sockets or not. We want to send them in new location
    useEffect(() => {
        if (shouldSendDataAboutLearnMoreClickedRef.current === cardKey) {
            shouldSendDataAboutLearnMoreClickedRef.current = undefined;
            removeKeyFromStorage(LEARN_MORE_CLICKED_KEY);
            handleLearnMoreDataSend();
        }
    }, [handleLearnMoreDataSend, cardKey]);

    useEffect(
        () => (): void => {
            clearTimeout(productAddedToCartTimerRef.current);
        },
        [],
    );

    const learnMoreButton = (
        <button type="button" css={styles.linkButton} onClick={handleLearnMore}>
            <Translation value="recommendProductLearnMore" fallback="Learn more" />
        </button>
    );

    return (
        <div css={styles.container}>
            {imageUrl && <img src={imageUrl} alt={title} css={styles.image} />}

            <div css={styles.contentContainer}>
                <div css={styles.textAndPricesContainer}>
                    <p css={[styles.getTitleText(isMobile), textStyles.text14Medium]}>{title}</p>
                    {formatter && (
                        <p css={[styles.priceText, textStyles.text14Medium]}>
                            {formatter.format(price / 100)}
                        </p>
                    )}
                </div>

                <div css={styles.buttonsContainer}>
                    {withAddToCartButton ? (
                        <>
                            <AddToCartButton
                                displayedVariantId={variantId}
                                messageId={messageId}
                                disabled={!isAddToCartEnabled}
                                onAddToCart={handleAddToCart}
                                isFlyMessage={isFlyMessage}
                            />
                            {learnMoreButton}
                        </>
                    ) : (
                        learnMoreButton
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProductCard;
